import React from "react";
import { Users, Bell, CalendarCheck, TrendingUp, MapPin, Clock, User, FileText } from "lucide-react";

const Frame = ({ children, label }) => (
  <div className="rounded-lg border border-white/10 bg-[#0f0f11] shadow-2xl shadow-black/50 overflow-hidden">
    <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10 bg-[#141416]">
      <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
      <span className="ml-3 text-[11px] text-muted-foreground truncate">{label}</span>
    </div>
    <div className="p-4 md:p-5">{children}</div>
  </div>
);

export const DashboardMock = () => {
  const stats = [
    { label: "Total Leads", value: "5", icon: Users, tint: "text-primary" },
    { label: "New Requests", value: "1", icon: Bell, tint: "text-yellow-400" },
    { label: "Jobs Booked", value: "1", icon: CalendarCheck, tint: "text-green-400" },
    { label: "Conversion", value: "20%", icon: TrendingUp, tint: "text-blue-400" },
  ];
  return (
    <Frame label="tradiedesk.app/dashboard">
      <p className="font-display text-2xl tracking-tight mb-0.5">COMMAND CENTER</p>
      <p className="text-xs text-muted-foreground mb-4">Every lead Ava captured, at a glance.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-[#18181b] border border-white/10 rounded-sm p-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <s.icon className={`h-3.5 w-3.5 ${s.tint}`} />
            </div>
            <span className="font-display text-2xl tracking-tight">{s.value}</span>
          </div>
        ))}
      </div>
      <div className="bg-[#18181b] border border-white/10 rounded-sm p-3">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2.5">Lead Status</p>
        {[["Hot", 92, "#f97316"], ["Warm", 40, "#eab308"], ["Cold", 18, "#3b82f6"]].map(([l, w, c]) => (
          <div key={l} className="flex items-center gap-2 mb-2 last:mb-0">
            <span className="text-[11px] w-10 text-muted-foreground">{l}</span>
            <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${w}%`, background: c }} />
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
};

export const BookingsMock = () => (
  <Frame label="tradiedesk.app/dashboard/bookings">
    <div className="flex items-center gap-2 mb-3">
      <p className="font-display text-2xl tracking-tight">BOOKINGS</p>
      <span className="text-[10px] font-bold bg-primary text-white rounded-sm px-1.5 py-0.5">1 NEW</span>
    </div>
    {[
      { badge: "Requested", bc: "bg-primary/20 text-primary", name: "Priya", phone: "07711 223344", job: "Leaking tap needs fixing", when: "25 Jul, 8:00 AM", where: "9 Elm St", cta: "Review & Approve", ctaClass: "bg-primary text-white" },
      { badge: "Approved", bc: "bg-blue-500/20 text-blue-400", name: "Jordan", phone: "07700 900123", job: "Boiler service", when: "31 Jul, 10:00 AM", where: "5 Oak Rd, Manchester", cta: "Confirm booking", ctaClass: "bg-green-500 text-white" },
    ].map((b) => (
      <div key={b.name} className="bg-[#18181b] border border-white/10 rounded-sm p-3.5 mb-2.5 last:mb-0">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-sm px-1.5 py-0.5 ${b.bc}`}>{b.badge}</span>
          <span className="text-[11px] font-semibold text-primary">Plumber</span>
        </div>
        <p className="text-sm font-medium mb-2">{b.job}</p>
        <div className="space-y-1 text-[11px] text-muted-foreground mb-3">
          <p className="flex items-center gap-1.5"><User className="h-3 w-3" /> {b.name} · {b.phone}</p>
          <p className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> {b.when}</p>
          <p className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {b.where}</p>
        </div>
        <div className={`text-center text-xs font-semibold rounded-sm py-2 ${b.ctaClass}`}>{b.cta}</div>
      </div>
    ))}
  </Frame>
);

export const CalendarMock = () => {
  const days = ["M", "T", "W", "T", "F", "S", "S"];
  const cells = Array.from({ length: 35 }, (_, i) => i - 2); // start offset
  return (
    <Frame label="tradiedesk.app/dashboard/calendar">
      <div className="flex items-center justify-between mb-3">
        <p className="font-display text-2xl tracking-tight">CALENDAR</p>
        <span className="text-xs text-muted-foreground">July 2026</span>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {days.map((d, i) => <span key={i} className="text-[10px] text-center text-muted-foreground uppercase">{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((n, i) => {
          const valid = n >= 1 && n <= 31;
          const hasBooking = n === 31;
          const blocked = n === 12 || n === 13;
          return (
            <div key={i} className={`aspect-square rounded-sm border text-[10px] p-1 flex flex-col ${valid ? "border-white/10 bg-[#18181b]" : "border-transparent"} ${blocked ? "!bg-red-500/15 !border-red-500/30" : ""}`}>
              {valid && <span className={hasBooking ? "text-primary font-semibold" : "text-muted-foreground"}>{n}</span>}
              {hasBooking && <span className="mt-auto text-[8px] bg-primary/25 text-primary rounded-sm px-0.5 truncate">10:00 Jordan</span>}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-500/40" /> Blocked</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-primary/40" /> Has bookings</span>
      </div>
    </Frame>
  );
};


export const InvoiceMock = () => (
  <Frame label="tradiedesk.app/dashboard/invoices">
    <div className="bg-white text-neutral-900 rounded-sm p-5" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="h-7 w-7 rounded bg-[#f97316] flex items-center justify-center mb-1.5"><FileText className="h-4 w-4 text-white" /></div>
          <p className="font-bold text-sm leading-tight">Apex Plumbing</p>
          <p className="text-[9px] text-neutral-500">12 High St, Manchester</p>
        </div>
        <div className="text-right">
          <p className="text-xl font-extrabold" style={{ color: "#f97316" }}>INVOICE</p>
          <p className="text-[10px] font-mono">INV-0042</p>
          <p className="text-[9px] text-neutral-500">Due: 14 Aug 2026</p>
        </div>
      </div>
      <p className="text-[9px] uppercase tracking-wider text-neutral-400">Bill to</p>
      <p className="text-xs font-semibold mb-3">Jordan Hughes</p>
      <table className="w-full text-[11px] mb-3" style={{ borderCollapse: "collapse" }}>
        <thead><tr style={{ borderBottom: "1.5px solid #111" }} className="text-left text-[9px] uppercase text-neutral-500"><th className="py-1">Description</th><th className="py-1 text-right">Qty</th><th className="py-1 text-right">Amount</th></tr></thead>
        <tbody>
          {[["Boiler service", "1", "£120.00"], ["Replacement valve", "2", "£45.00"]].map((r) => (
            <tr key={r[0]} style={{ borderBottom: "1px solid #eee" }}><td className="py-1.5">{r[0]}</td><td className="py-1.5 text-right">{r[1]}</td><td className="py-1.5 text-right">{r[2]}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-end">
        <div className="w-40 text-[11px]">
          <div className="flex justify-between py-0.5"><span className="text-neutral-500">Subtotal</span><span>£210.00</span></div>
          <div className="flex justify-between py-0.5"><span className="text-neutral-500">VAT (20%)</span><span>£42.00</span></div>
          <div className="flex justify-between py-1 font-bold text-sm" style={{ borderTop: "1.5px solid #111" }}><span>Total</span><span style={{ color: "#f97316" }}>£252.00</span></div>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2 mt-3">
      <div className="text-center text-[10px] font-semibold rounded-sm py-1.5 bg-[#25d366] text-white">WhatsApp</div>
      <div className="text-center text-[10px] font-semibold rounded-sm py-1.5 bg-primary text-white">Email</div>
      <div className="text-center text-[10px] font-semibold rounded-sm py-1.5 border border-white/15 text-muted-foreground">Save image</div>
    </div>
  </Frame>
);
